import { addTranslationKeys } from 'Src/i18n';
import { buildProductArgs } from 'Utils/graphql';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  LINE_PRODUCT_ACCOMMODATIONS,
  LINE_PRODUCT_ACCOMMODATIONS_STREETVIEWS,
} from 'Utils/request/requestDictionary';
export type CMSProps = {
  bathroomLabel: string;
  capacityOfPeopleLabel: string;
  strengthsLabel: string;
  amenitiesLabel: string;
  equipmentLabel: string;
  id?: string;
  isLineExclusiveCollection?: boolean;
  numberOfPeopleLabel: string;
  resortId: string;
  roomConfiguration: {
    singleRoom: string;
    connectingRooms: string;
    separatedRooms: string;
  };
  seeMore: string;
  servicesLabel: string;
};
type GQLProps = {
  accommodationCategory?: {
    rankedCategories?: Array<{
      id?: string;
      ranking?: string;
    }>;
  };
  config?: {
    common?: {
      backNavigation?: string;
      exclusiveCollectionLabel: string;
      viewMore?: string;
      loadLess?: string;
      next?: string;
      previous?: string;
      scrollBackward?: string;
      scrollForward?: string;
    };
    accommodations?: {
      surfaceLabel?: string;
    };
  };
  resort: {
    accommodationsIntroduction?: {
      description?: string;
      shortDescription?: string;
    };
    accommodations?: Array<Accommodation>;
    areas: Array<{
      id: string;
      label: string;
    }>;
    comfort?: number;
    id?: string;
    isExclusiveCollection?: boolean;
    type?: string;
  };
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
};
export type LineProps = CMSProps & GQLProps;

const getQuery = (productArgs) => {
  return `query {
    config {
      common {
        backNavigation
        exclusiveCollectionLabel
        viewMore
        loadLess
        next
        previous
        scrollBackward
        scrollForward
        roomPlan {
          title
          disclaimer
          swipeInstruction
        }
      }
      accommodations {
        surfaceLabel
      }
    }
    accommodationCategory(${productArgs}) {
      rankedCategories: accommodations {
        id
        ranking
      }
    }
    resort(${productArgs}) {
      areas {
        id
        label
      }
      id
      accommodationsIntroduction {
        description
        shortDescription
      }
      accommodations {
        areas {
          id
        }
        area {
          max
          min
          unit
        }
        bathroomEquipments {
          id
          label
        }
        category
        categoryId
        comfortId
        comfortType
        description
        equipments {
          id
          label
          ranking
        }
        exclusiveCollection
        floorPlans
        id
        label
        maxCapacity
        minCapacity
        media {
          photos {
            url
          }
        }
        ranking
        roomType
        services {
          id
          label
          ranking
        }
        outsideFacilities {
          id
          label
        }
        strengths {
          id
          label
        }
        surface
        view {
          id
          label
        }
      }
      comfort
      isExclusiveCollection
      type
    }
  }`;
};

const getStreetViewsQuery = (
  productArgs: string,
  accommodationCategory?: {
    rankedCategories?: Array<{
      id?: string;
      ranking?: string;
    }>;
  },
) => {
  const rankedIds =
    accommodationCategory?.rankedCategories?.map(({ id }) => (id ? `"${id}"` : '')) || [];
  return `query {
    streetViews(${productArgs}, accommodationCategoryIds:[${rankedIds.join(',')}]) {
      accommodationCategoryId
      streetView {
        latitude
        longitude
        heading
        pitch
        fov
        url
      }
    }
  }`;
};

const CRUISE_RESORT_ID = 'CM2C';
type ResolverFactory = (arg0: {
  isLineExclusiveCollection: boolean;
}) => Resolve<GQLProps, CMSProps, ProductRouterLocation>;
export const accommodationResolverFactory: ResolverFactory =
  ({ isLineExclusiveCollection }) =>
  async (appContext, props, _pageContext, location) => {
    const { urlParams } = location;
    const { resortId: productId } = props;
    const productArgs = buildProductArgs(productId, urlParams);
    const query = getQuery(productArgs);
    const lineData = await graphQLClient.runQuery(query, appContext, {
      requestName: LINE_PRODUCT_ACCOMMODATIONS,
      strictErrors: false,
    });
    addTranslationKeys(lineData.config);

    if (productId === CRUISE_RESORT_ID) {
      return { ...lineData, isLineExclusiveCollection };
    }

    const streetViewsQuery = getStreetViewsQuery(productArgs, lineData.accommodationCategory);
    const streeviewData = await graphQLClient.runQuery(streetViewsQuery, appContext, {
      requestName: LINE_PRODUCT_ACCOMMODATIONS_STREETVIEWS,
      strictErrors: false,
    });
    return { ...lineData, ...streeviewData, isLineExclusiveCollection };
  };
export const resolve = accommodationResolverFactory({
  isLineExclusiveCollection: false,
});
